/**
 * @component ry-sticky
 * @category  layout
 * @summary   Pin children to the top (or bottom) of the scroll container using CSS `position: sticky` (Fomantic UI `.ui.sticky` equivalent). Configure the pin edge via `[bottom]` and the offset via `[offset]`. Optionally observe the stuck state via an `IntersectionObserver` sentinel — fires `ry:stick` / `ry:unstick` when the element becomes pinned or unpins.
 *
 * Reference: Fomantic UI's sticky module. ry-sticky uses native CSS sticky
 * (`position: sticky`) instead of Fomantic's scroll-handler/`position: fixed`
 * approach — it works on every modern browser with no JS to position the
 * element, and gets two of Fomantic's headline behaviors for free:
 *
 *   1. CONTEXT BINDING. In Fomantic you pass a `context` container and the
 *      sticky pins to the viewport, then RELEASES when it collides with the
 *      context's bottom edge so it never overlaps following content. Native
 *      CSS sticky does the same thing automatically: the sticky element pins
 *      while you scroll through its *nearest scrolling ancestor* and is
 *      released at the bottom of that ancestor's padding-box. The "context"
 *      is therefore the scroll container — make `<ry-sticky>` a child of the
 *      element that scrolls (or let the page be that element). No `context`
 *      attribute needed; the DOM IS the context.
 *
 *   2. OFFSET / bottomOffset. Fomantic's `offset` / `bottomOffset` add a gap
 *      between the pinned edge and the viewport so the sticky parks below a
 *      fixed topbar instead of under it. ry-sticky's `[offset]` maps 1:1:
 *      `top: var(--ry-sticky-offset)` for top pins, `bottom: …` for `[bottom]`.
 *
 * Fomantic behaviors ry-sticky intentionally does NOT replicate (see @gotcha):
 *   - `pushing` mode (flip pin edge by scroll direction).
 *   - Tall-content scrubbing — when the sticky is TALLER than the viewport,
 *     Fomantic sticks the BOTTOM while scrolling down and the TOP while
 *     scrolling up so you can reach all of it. Native sticky can't; if you
 *     need it, the sticky content is probably too tall for a sticky and
 *     should scroll independently in its own overflow container instead.
 *
 * JS only kicks in to detect the stuck transition and emit `ry:stick` /
 * `ry:unstick` events when `[observe]` is set — it never positions the element.
 *
 * @attribute offset    string?    default="0"    Pixel offset from the pin edge (top or bottom). Accepts unit-less number (interpreted as `px`) or any CSS length (`"1rem"`, `"80px"`, `"var(--my-offset)"`). Drives the `--ry-sticky-offset` custom property the CSS reads.
 * @attribute bottom    boolean    default=false  When present, pins to the BOTTOM edge instead of the top. Mutually exclusive with the default top-pin behavior.
 * @attribute observe   boolean    default=false  When present, wires up an `IntersectionObserver` that fires `ry:stick` / `ry:unstick` as the element transitions between scrolled-into-view and pinned. Skip this attribute when you only need the visual pin — observers cost a little; CSS pins for free.
 *
 * @property  stuck     boolean    Read-only — true when the element is currently pinned (matters only when `[observe]` is set; always false otherwise).
 *
 * @event ry:stick
 *        detail: {}
 *        fires:  when the element transitions from "in normal flow" to "pinned to edge" (only when `[observe]` is set).
 *
 * @event ry:unstick
 *        detail: {}
 *        fires:  when the element returns to normal flow from pinned (only when `[observe]` is set).
 *
 * @state     `data-ry-state` of `"stuck"` when pinned (only when `[observe]` is set).
 *
 * @content   Free-form. ry-sticky is a thin wrapper that applies `position: sticky` to itself — children appear unchanged. Use plain HTML inside.
 *
 * @dom
 *   ry-sticky[offset?][bottom?][observe?][data-ry-state?]   role="region"
 *     > {children}
 *
 * @gotcha  `position: sticky` requires a scrolling ancestor (the "context"). If you place `<ry-sticky>` inside a parent that does NOT scroll — or whose own height is only as tall as its content — NOTHING WILL PIN, because there is no overflow to scroll past. The sticky element only sticks within its nearest scrolling ancestor's padding-box. Test by scrolling that container — not the page — unless the page IS the container. Also fatal: an ancestor with `overflow: hidden`/`clip` or a `transform`/`filter`/`contain` that creates a new containing block silently breaks sticky.
 *
 * @gotcha  OFFSET MUST MATCH A REAL OBSTRUCTION OR IT LOOKS BROKEN. `[offset]` pins the element `offset` pixels BELOW the scroll-box top (CSS `top: offset`). If there is nothing occupying those pixels — no fixed topbar, no second sticky pinned at top:0 — the header floats in the middle of the box with a dead gap above it and OVERLAPS the scrolling content. Only set `[offset]` equal to the height of something that is actually pinned above it. (This is exactly Fomantic's `offset`: a gap so the sticky parks *under* a topbar, never a standalone nudge.)
 *
 * @gotcha  DO NOT inherit `--ry-sticky-offset` from an ancestor. The component writes an inline `--ry-sticky-offset` on every instance (default `0px`), which masks an inherited value — but if a host stylesheet sets `--ry-sticky-offset` on a wrapper expecting it to flow down, it will be ignored. Use the `[offset]` attribute, not a cascaded custom property. (The kitchen-sink demo once set `--ry-sticky-offset: var(--header-h)` on every example cell; it pushed each pinned header ~60px down inside its own box. Removed.)
 *
 * @gotcha  TALL CONTENT. If the sticky element is taller than its scroll container's viewport, native CSS sticky will pin its TOP and the overflowing bottom is simply unreachable while pinned (unlike Fomantic, which scrubs top/bottom by scroll direction). For tall pinned content, give the element its own `max-height` + `overflow:auto` so it scrolls internally instead of relying on sticky to reveal it.
 *
 * @gotcha  `<ry-sticky>` does NOT snapshot its offset — `--ry-sticky-offset` is live. CSS re-reads it on every attribute change, so updating `[offset]` re-pins immediately at the new edge.
 *
 * @gotcha  `[observe]` adds an IntersectionObserver that watches a sentinel sibling div inserted BEFORE the sticky element. If you place `<ry-sticky>` as the FIRST child of its parent and the parent has no other children before it, the sentinel placement still works — but the observer fires "stuck" immediately because the sentinel is at scrollTop=0 from the get-go. This is fine in practice but can surprise tests; if you need precise control, observe manually.
 *
 * @gotcha  `[bottom]` only flips the CSS pin edge — the IntersectionObserver semantics still observe the TOP sentinel by default. Bottom-pinned stuck detection is intentionally not modelled (pin-to-bottom is rare and the observer logic differs).
 *
 * @example basic
 * ```html
 * <div style="overflow:auto; height:300px; border:1px solid var(--ry-color-border); padding:0.5rem;">
 *   <ry-sticky style="background:var(--ry-color-bg); padding:0.5rem; border-bottom:1px solid var(--ry-color-border);">Pinned header — scroll inside this box</ry-sticky>
 *   <div style="padding:1rem;">
 *     <p>Scroll inside this box to see the pinning behavior.</p>
 *     <p>Line 1</p><p>Line 2</p><p>Line 3</p><p>Line 4</p><p>Line 5</p>
 *     <p>Line 6</p><p>Line 7</p><p>Line 8</p><p>Line 9</p><p>Line 10</p>
 *     <p>Line 11</p><p>Line 12</p><p>Line 13</p><p>Line 14</p><p>Line 15</p>
 *     <p>Line 16</p><p>Line 17</p><p>Line 18</p><p>Line 19</p><p>Line 20</p>
 *   </div>
 * </div>
 * ```
 *
 * @example with-offset
 * ```html
 * <!-- offset="64" pins the header 64px below the scroll-box top — exactly the
 *      height of the topbar that ALSO sticks at top:0. Two stacked sticky
 *      layers: a topbar at 0, a section header at 64. Scroll to watch the
 *      header park under the topbar instead of overlapping it. -->
 * <div style="overflow:auto; height:300px; border:1px solid var(--ry-color-border); position:relative;">
 *   <ry-sticky style="display:flex; align-items:center; height:64px; padding:0 1rem; background:var(--ry-color-primary, #333); color:#fff;">Topbar (64px tall, pinned at top)</ry-sticky>
 *   <ry-sticky offset="64" style="background:var(--ry-color-bg); padding:0.5rem 1rem; border-bottom:1px solid var(--ry-color-border);">Section header — pinned 64px below, under the topbar</ry-sticky>
 *   <div style="padding:1rem;">
 *     <p>Scroll to see both layers pin without overlapping.</p>
 *     <p>Line 1</p><p>Line 2</p><p>Line 3</p><p>Line 4</p><p>Line 5</p>
 *     <p>Line 6</p><p>Line 7</p><p>Line 8</p><p>Line 9</p><p>Line 10</p>
 *     <p>Line 11</p><p>Line 12</p><p>Line 13</p><p>Line 14</p><p>Line 15</p>
 *   </div>
 * </div>
 * ```
 *
 * @example pin-to-bottom
 * ```html
 * <div style="overflow:auto; height:300px; border:1px solid var(--ry-color-border);">
 *   <div style="padding:1rem;">
 *     <p>Scroll up to see the footer pin.</p>
 *     <p>Line 1</p><p>Line 2</p><p>Line 3</p><p>Line 4</p><p>Line 5</p>
 *     <p>Line 6</p><p>Line 7</p><p>Line 8</p><p>Line 9</p><p>Line 10</p>
 *     <p>Line 11</p><p>Line 12</p><p>Line 13</p><p>Line 14</p><p>Line 15</p>
 *   </div>
 *   <ry-sticky bottom style="background:var(--ry-color-bg); padding:0.5rem; border-top:1px solid var(--ry-color-border);">Pinned footer</ry-sticky>
 * </div>
 * ```
 *
 * @tutorial mount-applies-role-region
 * ```js
 * const el = mount('<ry-sticky>content</ry-sticky>');
 * await expectAttr(el, 'role', 'region', 'role="region" on the wrapper');
 * ```
 *
 * @tutorial offset-applies-as-css-variable
 * ```js
 * const el = mount('<ry-sticky offset="80">x</ry-sticky>');
 * await expectEqual(el.style.getPropertyValue('--ry-sticky-offset'), '80px', 'numeric offset gets px suffix');
 * ```
 *
 * @tutorial offset-respects-css-length-syntax
 * ```js
 * // Wrong assumption: ry-sticky always tacks "px" on. CSS-length values
 * // (with a unit) are passed through unchanged.
 * const el = mount('<ry-sticky offset="4rem">x</ry-sticky>');
 * await expectEqual(el.style.getPropertyValue('--ry-sticky-offset'), '4rem', 'unit preserved');
 *
 * const elVar = mount('<ry-sticky offset="var(--topbar-height)">x</ry-sticky>');
 * await expectEqual(elVar.style.getPropertyValue('--ry-sticky-offset'), 'var(--topbar-height)', 'CSS var passed through');
 * ```
 *
 * @tutorial default-offset-is-0px
 * ```js
 * const el = mount('<ry-sticky>x</ry-sticky>');
 * await expectEqual(el.style.getPropertyValue('--ry-sticky-offset'), '0px', 'default offset is 0px');
 * ```
 *
 * @tutorial bottom-flips-pin-edge
 * ```js
 * // [bottom] is a pure CSS hook — JS just exposes the attribute presence.
 * const top = mount('<ry-sticky>x</ry-sticky>');
 * await expectEqual(top.hasAttribute('bottom'), false);
 * const bot = mount('<ry-sticky bottom>x</ry-sticky>');
 * await expectEqual(bot.hasAttribute('bottom'), true, '[bottom] survives on the host');
 * ```
 *
 * @tutorial observer-skipped-without-attribute
 * ```js
 * // Wrong assumption: every ry-sticky observes stuck state. The observer
 * // is opt-in via [observe] — without it, .stuck is always false and no
 * // sentinel sibling is inserted.
 * const el = mount('<div><ry-sticky>x</ry-sticky></div>');
 * const sticky = el.querySelector('ry-sticky');
 * await expectEqual(sticky.stuck, false);
 * // No sentinel div should be inserted before the sticky
 * await expectEqual(el.querySelector('[data-ry-target="sticky-sentinel"]'), null, 'no sentinel without [observe]');
 * ```
 *
 * @tutorial observer-inserts-sentinel
 * ```js
 * // With [observe], a 1px sentinel div is inserted BEFORE the sticky so the
 * // observer can detect when the parent has scrolled past the sticky's
 * // natural position.
 * const el = mount('<div><ry-sticky observe>x</ry-sticky></div>');
 * await sleep(20);  // wait for setup() to run
 * await expectEqual(!!el.querySelector('[data-ry-target="sticky-sentinel"]'), true, 'sentinel inserted with [observe]');
 * ```
 *
 * @tutorial live-offset-update
 * ```js
 * const el = mount('<ry-sticky offset="40">x</ry-sticky>');
 * await expectEqual(el.style.getPropertyValue('--ry-sticky-offset'), '40px');
 * el.setAttribute('offset', '128');
 * await expectEqual(el.style.getPropertyValue('--ry-sticky-offset'), '128px', 'live update reflected');
 * el.setAttribute('offset', '8rem');
 * await expectEqual(el.style.getPropertyValue('--ry-sticky-offset'), '8rem', 'unit change honored');
 * ```
 *
 * @see ry-drawer
 * @see ry-modal
 */

import { RyElement } from '../core/ry-element.js';

const HAS_CSS_UNIT = /^[+-]?\d*\.?\d+([a-zA-Z]+|%)$/;

export class RySticky extends RyElement {
  static get observedAttributes(): string[] {
    return ['offset', 'observe'];
  }

  #observer: IntersectionObserver | null = null;
  #sentinel: HTMLElement | null = null;

  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'region');
    this.#applyOffset();
    if (this.hasAttribute('observe')) this.#startObserving();
  }

  teardown(): void {
    this.#stopObserving();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;

    if (name === 'offset') {
      this.#applyOffset();
    } else if (name === 'observe') {
      if (this.hasAttribute('observe')) this.#startObserving();
      else this.#stopObserving();
    }
  }

  get stuck(): boolean {
    return this.dataset.ryState === 'stuck';
  }

  #applyOffset(): void {
    const raw = (this.getAttribute('offset') ?? '0').trim();
    let value: string;
    if (raw === '' || raw === '0') {
      value = '0px';
    } else if (HAS_CSS_UNIT.test(raw)) {
      value = raw;
    } else if (/^[+-]?\d*\.?\d+$/.test(raw)) {
      value = `${raw}px`;
    } else {
      // Unrecognized — pass through (could be a calc(), var(), etc.)
      value = raw;
    }
    this.style.setProperty('--ry-sticky-offset', value);
  }

  #startObserving(): void {
    if (this.#observer || typeof IntersectionObserver === 'undefined') return;
    const parent = this.parentElement;
    if (!parent) return;

    this.#sentinel = document.createElement('div');
    this.#sentinel.setAttribute('data-ry-target', 'sticky-sentinel');
    this.#sentinel.setAttribute('aria-hidden', 'true');
    this.#sentinel.style.cssText = 'height:1px;width:100%;';
    parent.insertBefore(this.#sentinel, this);

    this.#observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const wasStuck = this.dataset.ryState === 'stuck';
      const isStuck = !entry.isIntersecting;
      if (isStuck && !wasStuck) {
        this.dataset.ryState = 'stuck';
        this.emit('stick');
      } else if (!isStuck && wasStuck) {
        delete this.dataset.ryState;
        this.emit('unstick');
      }
    });
    this.#observer.observe(this.#sentinel);
  }

  #stopObserving(): void {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
    if (this.#sentinel && this.#sentinel.parentElement) {
      this.#sentinel.remove();
      this.#sentinel = null;
    }
    if (this.dataset.ryState === 'stuck') delete this.dataset.ryState;
  }
}

customElements.define('ry-sticky', RySticky);
